/**
 * ============================================================================
 * PromotionsNew - PRODUCTION-READY PROMOTIONS PAGE (MVP)
 * ============================================================================
 *
 * Production-ready promotions management with:
 * - Time-Based Promotions (sport-specific, day/time filtering)
 * - Recurring Booking Discounts (per-court configuration)
 * - TanStack Query for data fetching
 * - CRUD operations with mutations
 * - Proper error handling
 * - 100% type safety
 *
 * ============================================================================
 */

import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useVenueRole } from '../hooks/useVenueRole';
import { AlertCircle, RefreshCw, Plus } from 'lucide-react';
import { usePromotions } from '../hooks/usePromotions';
import { useCourtsData } from '../hooks/useCourtsData';
import { PromotionStats } from '../components/promotions/PromotionStats';
import { PromotionsTable } from '../components/promotions/PromotionsTable';
import { RecurringDiscountsSection } from '../components/promotions/RecurringDiscountsSection';
import { TimeBasedPromotionFormModal } from '../components/promotions/TimeBasedPromotionFormModal';
import type { PromotionFilters, Promotion } from '../types/promotions';

/**
 * Promotions page - production-ready MVP version
 */
export default function PromotionsNew() {
  const { user } = useAuth();
  const { can } = useVenueRole();
  const venue = user?.venues?.[0];
  // Courts here are a SECONDARY read (for the recurring-discounts section) and
  // need schedule.view/venue.edit — a promotions-only staff shouldn't 403 the
  // whole page on it.
  const canSeeCourts = can('schedule.view') || can('venue.edit');

  // ============================================================================
  // STATE
  // ============================================================================

  const [filters, setFilters] = useState<PromotionFilters>({
    search: '',
    status: 'all',
    sport: 'all',
  });

  const [showPromotionModal, setShowPromotionModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null);

  // ============================================================================
  // DATA FETCHING (TanStack Query)
  // ============================================================================

  // Fetch time-based promotions
  const {
    data: promotionsData,
    isPending: promotionsPending,
    error: promotionsError,
    refetch: refetchPromotions,
  } = usePromotions(venue?.id, filters);

  // Fetch courts data (for recurring discounts) — only when the user can read
  // courts; otherwise skip it so its 403 doesn't blank the promotions page.
  const {
    data: courtsData,
    isPending: courtsPending,
  } = useCourtsData(canSeeCourts ? venue?.id : undefined);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const isPending = promotionsPending || courtsPending;
  // Courts is secondary — its failure must not blank the promotions page.
  const error = promotionsError;

  // Calculate stats
  const timeBasedActive = promotionsData?.stats.activePromotions || 0;
  const courtsWithRecurring =
    courtsData?.courts?.filter((court) => court.allow_recurring_bookings).length || 0;
  const totalActive = timeBasedActive + courtsWithRecurring;

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleFilterChange = (newFilters: Partial<PromotionFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const handleCreatePromotion = () => {
    setEditingPromotion(null);
    setShowPromotionModal(true);
  };

  const handleEditPromotion = (promotion: Promotion) => {
    setEditingPromotion(promotion);
    setShowPromotionModal(true);
  };

  const handleCloseModal = () => {
    setShowPromotionModal(false);
    setEditingPromotion(null);
  };

  // ============================================================================
  // LOADING STATE
  // ============================================================================

  if (isPending) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading promotions...</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // ERROR STATE
  // ============================================================================

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Promotions</h2>
        <p className="text-gray-600 mb-4">
          {error instanceof Error ? error.message : 'An error occurred'}
        </p>
        <button
          onClick={() => refetchPromotions()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // ============================================================================
  // NO VENUE STATE
  // ============================================================================

  if (!venue) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <AlertCircle className="w-12 h-12 text-gray-400 mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">No Venue Found</h2>
        <p className="text-gray-600">Please ensure you have a venue associated with your account.</p>
      </div>
    );
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-gray-900 mb-1">Promotions & Discounts</h1>
            <p className="text-gray-600 text-xs sm:text-sm">
              Manage time-based promotions and recurring booking discounts
            </p>
          </div>
          <button
            onClick={handleCreatePromotion}
            className="px-3 sm:px-4 py-2 sm:py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 text-sm sm:text-base"
          >
            <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">New Time-Based Promotion</span>
            <span className="sm:hidden">New Promotion</span>
          </button>
        </div>

        {/* Stats Cards */}
        <PromotionStats
          timeBasedActive={timeBasedActive}
          recurringCourts={courtsWithRecurring}
          totalActive={totalActive}
        />
      </div>

      {/* Time-Based Promotions Section */}
      <div>
        <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-2 sm:mb-3 flex items-center">
          ⏰ Time-Based Promotions
        </h2>
        <PromotionsTable
          promotions={promotionsData?.promotions || []}
          filters={filters}
          onFilterChange={handleFilterChange}
          venueId={venue.id}
          onEdit={handleEditPromotion}
        />
      </div>

      {/* Recurring Discounts Section */}
      <div className="pt-4 sm:pt-6 border-t border-gray-200">
        <RecurringDiscountsSection courts={courtsData?.courts || []} />
      </div>

      {/* Time-Based Promotion Form Modal */}
      <TimeBasedPromotionFormModal
        isOpen={showPromotionModal}
        onClose={handleCloseModal}
        venueId={venue.id}
        sports={venue.sports || []}
        editingPromotion={editingPromotion}
      />
    </div>
  );
}
